import qrcode from "qrcode-generator";

export type QrRun = { x: number; y: number; w: number };

export type QrModules = {
  // Total width/height in pixels
  size: number;
  // Pixels per module (and the height of every run)
  cell: number;
  // Dark modules, merged into horizontal runs per row
  runs: QrRun[];
};

/**
 * Encodes `text` as a QR code and returns its dark modules as rectangles,
 * so it can be drawn with plain colored Views.
 *
 * Plain Views are used instead of an image because some Android WebViews
 * can't load a generated image: the renderer loads `data:` URLs in an image
 * worker (XHR + createImageBitmap), and on older WebViews that fails and
 * shows nothing.
 *
 * Each module is a whole number of pixels so the code stays sharp, and the
 * whole code is never larger than `maxSize`. No quiet zone is added: put it
 * on a white background with padding instead.
 */
export function createQrModules(text: string, maxSize: number): QrModules {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();

  const count = qr.getModuleCount();
  const cell = Math.max(1, Math.floor(maxSize / count));
  const runs: QrRun[] = [];

  for (let row = 0; row < count; row++) {
    let col = 0;
    while (col < count) {
      if (!qr.isDark(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < count && qr.isDark(row, col)) col++;
      runs.push({ x: start * cell, y: row * cell, w: (col - start) * cell });
    }
  }

  return { size: count * cell, cell, runs };
}
